import { ReactNode, useEffect, useRef, useState } from "react";
import styles from "./dropdown.module.css";

const dropdownArrowStyle = {
  up: "rotate(-180deg)",
  down: "rotate(0deg)",
};

interface SelectDropdownProps {
  defaultOptionValue?: string;
  defaultOptionText?: string;
  dropdownOptions?: Array<{ Key: string; Value: string }>;
  onChange?: (selectedOptionValue: string) => void;
  selectedValue?: string;
  customOptions?: ReactNode;
  optionsParentDivClassName?: string;
  dropdownParentDivClassName?: string;
  isSmView?: boolean;
}

const SelectDropdown = ({
  defaultOptionValue,
  defaultOptionText,
  dropdownOptions,
  onChange,
  selectedValue,
  customOptions,
  optionsParentDivClassName,
  dropdownParentDivClassName,
  isSmView,
}: SelectDropdownProps) => {
  const [dropodownArrowState, setDropdownArrowState] = useState<keyof typeof dropdownArrowStyle>("down");
  const [showOptions, setShowOptions] = useState(false);
  const dropdownParentDivRef = useRef<HTMLDivElement>();

  useEffect(() => {
    document.addEventListener("click", ({ target }: MouseEvent) => {
      if (!dropdownParentDivRef.current?.contains(target as Node)) {
        updateArrowAndOptionsState("down", false);
      }
    });
  }, []);

  const updateArrowAndOptionsState = (arrowState: keyof typeof dropdownArrowStyle, optionState: boolean) => {
    setDropdownArrowState(arrowState);
    setShowOptions(optionState);
  };

  if (isSmView) {
    return (
      <div
        className={`flex items-center cursor-pointer relative ${styles.dropdownParentDiv} ${dropdownParentDivClassName}`}
        onClick={e => {
          e.stopPropagation();
          setDropdownArrowState(prev => (prev === "up" ? "down" : "up"));
          setShowOptions(prev => !prev);
        }}
        ref={dropdownParentDivRef}>
        <img src={"/assets/images/wac/sort-by-orange.png"} alt="SortBy" style={{ maxWidth: "100%", height: "auto" }} />
        {showOptions && (
          <div className={`absolute z-10 top-12 ${optionsParentDivClassName}`}>
            <ul
              className={`bg-white border-solid border-1 border-gray-600 rounded ${styles.sortByOptionsContainer}`}
              data-testid="list-options-container">
              {dropdownOptions.map(option => (
                <li key={option.Key} className={`mx-1 p-1`} onClick={() => onChange(option.Key)}>
                  {option.Value}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
  const dropdownText =
    (selectedValue !== "" && dropdownOptions?.find(option => option.Key === selectedValue)?.Value) ||
    (selectedValue !== "" ? selectedValue : defaultOptionText);
  return (
    <div
      className={`flex cursor-pointer relative ${styles.dropdownParentDiv} ${dropdownParentDivClassName}`}
      onClick={() => {
        setDropdownArrowState(prev => (prev === "up" ? "down" : "up"));
        setShowOptions(prev => !prev);
      }}
      ref={dropdownParentDivRef}
      data-testid="dropdown-container">
      <img
        src={"/assets/images/select-arrow.png"}
        alt="arrow-dropdown"
        className="self-center mx-2"
        data-testid={`dropdown-state-${dropodownArrowState}`}
        style={{
          transform: dropdownArrowStyle[dropodownArrowState],
        }}
      />
      <span className="self-center w-max truncate" data-testid="dropdownDisplayText" title={dropdownText}>
        {dropdownText}
      </span>
      {selectedValue !== defaultOptionValue && (
        <img
          data-testid="clear-category-filter"
          className="cursor-pointer self-center ml-auto mr-4"
          src={"/assets/images/select-cross-orange.png"}
          alt="cross-icon"
          width={11}
          height={11}
          onClick={() => onChange(defaultOptionValue)}
        />
      )}
      {showOptions && (
        <div
          className={`absolute z-[100] top-9 border-[2px] border-solid border-[#c7c7c7] rounded ${optionsParentDivClassName}`}>
          {customOptions || (
            <ul className={`bg-white ${styles.sortByOptionsContainer}`} data-testid="list-options-container">
              {dropdownOptions?.map(option => (
                <li
                  data-testid={option.Key}
                  key={option.Key}
                  className={`mx-1 p-1`}
                  onClick={e => {
                    e.stopPropagation();
                    onChange(option.Key);
                    updateArrowAndOptionsState("down", false);
                  }}>
                  {option.Value}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SelectDropdown;
